import React, { useState } from 'react';
import BentoCard from '../components/BentoCard';
import { Link } from 'react-router-dom';

// Image Imports
import packImage from '../images/positivePacks/PositivePack.jpg';
import stoneImage from '../images/positivePacks/BreatheStoneCard.jpg';
import schoolImage from '../images/schoolProgram.png';
import tableImage from '../images/events/eventTable.jpg';

// Emoji Imports
import sunEmoji from '../images/emojis/Meet_Sunny.png';
import cloudEmoji from '../images/emojis/Meet_Cloudy.png';
import stormEmoji from '../images/emojis/Meet_Stormy.png';
import rainbowEmoji from '../images/emojis/Meet_Rainbow.png';
import tornadoEmoji from '../images/emojis/Meet_Twister.png';
import windyEmoji from '../images/emojis/Meet_Windy.png';
import rainyEmoji from '../images/emojis/Meet_Rainy.png';
import partlySunnyEmoji from '../images/emojis/Meet_Partly_Sunny.png';

const Programs = () => {
  // State to manage the full-screen image modal
  const [selectedImage, setSelectedImage] = useState(null);

  // Helper function to close modal
  const closeModal = () => setSelectedImage(null);

  // Data array for Emojis
  const emojiData = [
    { src: sunEmoji, label: "Sunny", color: "text-accent-orange", delay: "delay-0" },
    { src: cloudEmoji, label: "Cloudy", color: "text-gray-600", delay: "delay-75" },
    { src: rainbowEmoji, label: "Rainbow", color: "text-purple-600", delay: "delay-100" },
    { src: stormEmoji, label: "Stormy", color: "text-primary-navy", delay: "delay-150" },
    { src: tornadoEmoji, label: "Tornado", color: "text-gray-800", delay: "delay-200" },
    { src: windyEmoji, label: "Windy", color: "text-gray-800", delay: "delay-200" },
    { src: rainyEmoji, label: "Rainy", color: "text-gray-800", delay: "delay-200" },
    { src: partlySunnyEmoji, label: "Partly Sunny", color: "text-gray-800", delay: "delay-200" },
  ];

  return (
    <main className="max-w-7xl mx-auto p-4 lg:p-8 space-y-12">
      
      {/* --- IMAGE MODAL (LIGHTBOX) --- */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          onClick={closeModal}
        >
          <button 
            onClick={closeModal} 
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <img 
            src={selectedImage} 
            alt="Full view" 
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl cursor-default" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

      {/* HEADER */}
      <div className="text-center max-w-3xl mx-auto mb-8">
        <h1 className="text-5xl font-heading font-bold text-primary-navy mb-4">Our Programs</h1>
        <p className="text-xl font-bold text-gray-700">
          Practical tools and hands-on learning to help kids build resilience, one small step at a time.
        </p>
      </div>

      {/* PROGRAM 1: THE POSITIVE PACK */}
      <div id="positive-pack" className="scroll-mt-24">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-1 w-12 bg-green-500 rounded-full"></div>
          <h2 className="text-3xl font-heading font-bold text-primary-navy">The Positive Pack</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Main Visual */}
          <BentoCard colSpan="lg:col-span-5" className="p-0 overflow-hidden min-h-[400px]">
            <img src={packImage} alt="Positive Pack Backpack" className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
          </BentoCard>

          {/* Description & Contents */}
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <BentoCard colSpan="md:col-span-2" className="bg-white border-2 border-primary-navy">
              <h3 className="text-2xl font-bold mb-4 text-primary-navy">Tools for Everyday Coping</h3>
              <p className="leading-relaxed mb-4 text-gray-700 font-medium">
                The Positive Pack is designed to give kids and families a simple, supportive way to practice mental wellness at home. 
                Each pack is filled with hands‑on tools that make coping skills easy to learn.
              </p>
            </BentoCard>

            <BentoCard title="What's Inside?" className="bg-blue-50 border-l-4 border-primary-navy">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-accent-orange text-xl">✍️</span>
                  <span className="text-sm font-bold text-primary-navy">Custom Journal & Writing Tools</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-orange text-xl">🌀</span>
                  <span className="text-sm font-bold text-primary-navy">Stress Reliever Fidgets</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-orange text-xl">💤</span>
                  <span className="text-sm font-bold text-primary-navy">Sleep Mask & Hygiene Tips</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-orange text-xl">🌟</span>
                  <span className="text-sm font-bold text-primary-navy">Affirmation Bracelet</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-orange text-xl">✨</span>
                  <span className="text-sm font-bold text-primary-navy">And More... Educational & Coping Tools</span>
                </li>
              </ul>
            </BentoCard>

            {/* BREATHE STONE */}
            <BentoCard className="p-0 overflow-hidden flex flex-col bg-white border-2 border-primary-navy/10">
               <div className="flex-grow bg-gray-100 relative group cursor-pointer" onClick={() => setSelectedImage(stoneImage)}>
                  <img src={stoneImage} alt="Breathing Stone" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 bg-white p-2 rounded-full shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-all">🔍</span>
                  </div>
               </div>
               <div className="p-4 bg-primary-navy text-white">
                  <h4 className="font-bold text-lg text-accent-yellow mb-1">The Breathe Stone</h4>
                  <p className="text-blue-100 text-sm">A tactile tool included in every pack for grounding exercises.</p>
               </div>
            </BentoCard>
          </div>
        </div>
      </div>

      {/* PROGRAM 2: THE WEATHER WITHIN */}
      <div id="weather-within" className="scroll-mt-24">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-1 w-12 bg-fuchsia-900 rounded-full"></div>
          <h2 className="text-3xl font-heading font-bold text-primary-navy">The Weather Within</h2>
        </div>

        <BentoCard className="bg-gradient-to-br from-blue-50 to-white overflow-hidden relative border border-blue-100">
          <div className="flex flex-col gap-8 relative z-10">
            
            <div className="max-w-3xl">
              <h3 className="text-2xl font-bold text-primary-navy mb-4">Navigating Inner Storms</h3>
              <p className="text-primary-navy/80 mb-6 leading-relaxed font-medium">
                Using simple weather emojis, we give kids an easy language to recognize and express how they’re feeling on the inside. 
                From there, we guide them through breathing techniques and positive coping strategies to help &quot;change the weather.&quot;
              </p>
              <div className="flex flex-wrap gap-4">
                <span className="px-4 py-2 bg-white border border-blue-100 rounded-full text-sm font-bold text-primary-navy shadow-sm">☁️ Validation</span>
                <span className="px-4 py-2 bg-white border border-blue-100 rounded-full text-sm font-bold text-primary-navy shadow-sm">🌬️ Breathing</span>
                <span className="px-4 py-2 bg-white border border-blue-100 rounded-full text-sm font-bold text-primary-navy shadow-sm">⚓ Grounding</span>
              </div>
              <p className="mt-4 text-xs font-bold text-accent-orange uppercase tracking-wider animate-pulse">
                  Click any flyer below to view full details
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {emojiData.map((emoji, index) => (
                <button 
                  key={index}
                  onClick={() => setSelectedImage(emoji.src)}
                  className="text-center group focus:outline-none"
                  aria-label={`View ${emoji.label} flyer`}
                >
                  <div className={`bg-white p-2 rounded-2xl shadow-md mb-2 group-hover:-translate-y-2 transition-transform h-48 flex items-center justify-center relative overflow-hidden border-2 border-transparent group-hover:border-accent-yellow ${emoji.delay}`}>
                      <img src={emoji.src} alt={emoji.label} className="max-w-full max-h-full object-contain" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2 shadow-lg transform scale-50 group-hover:scale-100 transition-all duration-300">
                           🔍
                        </span>
                      </div>
                  </div>
                  <span className={`font-heading font-bold ${emoji.color}`}>{emoji.label}</span>
                </button>
              ))}
            </div>

          </div>
        </BentoCard>
      </div>

      {/* PROGRAM 3: SCHOOL PROGRAM */}
      <div id="school-program">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-1 w-12 bg-primary-forest rounded-full"></div>
          <h2 className="text-3xl font-heading font-bold text-primary-navy">Mental Wellness School Program</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           <BentoCard className="flex flex-col justify-center bg-white border-l-8 border-primary-forest">
              <h3 className="text-2xl font-bold mb-4 text-primary-forest">Educating for Wellness</h3>
              <p className="text-primary-navy mb-6 font-medium">
                  Positive Somebody’s Mental Wellness School program aims to educate students on the importance of positive coping and the role it plays in achieving mental wellness.
                  This is a hands-on, activity-based program.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <span className="text-primary-forest text-xl">🧠</span>
                    <span className="font-bold text-primary-navy">Recognizing and processing feelings</span>
                </li>
                <li className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <span className="text-primary-forest text-xl">🛠️</span>
                    <span className="font-bold text-primary-navy">Positive coping strategies</span>
                </li>
                <li className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <span className="text-primary-forest text-xl">🧘</span>
                    <span className="font-bold text-primary-navy">Mindfulness routines</span>
                </li>
                <li className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <span className="text-primary-forest text-xl">🍎</span>
                    <span className="font-bold text-primary-navy">Physical & nutritional health aspects</span>
                </li>
              </ul>
           </BentoCard>
           
           <BentoCard className="p-0 overflow-hidden min-h-[400px]">
              <img src={schoolImage} alt="School Program Activity" className="w-full h-full object-cover" />
           </BentoCard>
        </div>
      </div>

      {/* PROGRAM 4: HIGH SCHOOL CHAPTERS */}
      <div id="high-school-chapters">
         <div className="flex items-center gap-4 mb-6">
          <div className="h-1 w-12 bg-accent-orange rounded-full"></div>
          <h2 className="text-3xl font-heading font-bold text-primary-navy">High School Chapters</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <BentoCard colSpan="lg:col-span-4" className="p-0 overflow-hidden min-h-[300px]">
                <img src={tableImage} alt="High School Chapter Event" className="w-full h-full object-cover" />
            </BentoCard>

            <BentoCard colSpan="lg:col-span-8" className="bg-orange-50">
                <h3 className="text-2xl font-bold mb-4 text-accent-orange">Student-Led Leadership</h3>
                <p className="text-primary-navy mb-6 font-medium">
                    Our High School Chapters operate like vibrant school clubs, offering a range of enriching activities and opportunities for students to become leaders in mental health advocacy.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                        <h4 className="font-bold text-primary-navy mb-2">📢 Positive Promotion</h4>
                        <p className="text-sm text-gray-700">Advocate for initiatives that encourage mental wellness and positivity.</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                        <h4 className="font-bold text-primary-navy mb-2">🤝 Community Outreach</h4>
                        <p className="text-sm text-gray-700">Partner with middle and elementary schools to support younger students.</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                        <h4 className="font-bold text-primary-navy mb-2">📚 Educational Lessons</h4>
                        <p className="text-sm text-gray-700">Participate in informative sessions that foster personal growth.</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-orange-100">
                        <h4 className="font-bold text-primary-navy mb-2">🎨 Coping Activities</h4>
                        <p className="text-sm text-gray-700">Engage in activities designed to build resilience.</p>
                    </div>
                </div>
                <div className="mt-6 pt-6 border-t border-orange-200">
                    <p className="font-bold text-primary-navy">Want to start a chapter? <a 
                        href="mailto:beapositivesomebody@gmail.com?subject=Inquiry%3A%20Starting%20a%20High%20School%20Chapter&body=Hello%20Positive%20Somebody%20Team%2C%0D%0A%0D%0AI%20am%20interested%20in%20starting%20a%20high%20school%20chapter%20at%20%5BInsert%20School%20Name%5D.%20Please%20send%20me%20information%20on%20next%20steps.%0D%0A%0D%0AThank%20you%2C%0D%0A%5BYour%20Name%5D" 
                        className="text-accent-orange underline hover:text-accent-orange/80"
                      >
                        Contact us
                      </a></p>
                </div>
            </BentoCard>
        </div>
      </div>

      {/* PROGRAM 5: GRANT - Glassy Yellow */}
      <BentoCard className="bg-accent-yellow/20 backdrop-blur-md text-center py-12 border-[3px] border-accent-yellow">
          <h2 className="text-3xl font-heading font-black mb-4 text-primary-navy">The Roberson-Bell Grant</h2>
          <p className="text-lg text-primary-navy max-w-2xl mx-auto font-medium">
            This grant aims to provide positive outlets, such as music, art, sports, and other activities to youth that are unable to afford it otherwise.
          </p>
      </BentoCard>

      {/* CTA SECTION - Glassy Forest Green */}
      <BentoCard className="bg-primary-forest/20 backdrop-blur-md text-center py-16 border-[3px] border-primary-forest relative overflow-hidden">
        {/* Subtle texture */}
        <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none"></div>
        
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="text-4xl font-heading font-black mb-4 text-primary-navy">Partner With Us</h2>
          <p className="mb-8 text-xl text-primary-navy font-medium">
            Whether you are a school looking for programming or a student looking to lead, we have a place for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:beapositivesomebody@gmail.com?subject=Partnership%20Inquiry%3A%20School%20Programs&body=Hello%2C%0D%0A%0D%0AI%20represent%20%5BSchool%2FOrganization%20Name%5D%20and%20would%20like%20to%20request%20information%20about%20bringing%20Positive%20Somebody%20programs%20to%20our%20students.%0D%0A%0D%0ABest%20regards%2C%0D%0A%5BYour%20Name%5D" 
              className="bg-accent-yellow text-primary-navy px-8 py-3 rounded-full font-bold hover:bg-yellow-300 transition-colors shadow-[4px_4px_0px_#020617] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none border-2 border-primary-navy"
            >
              Request Info
            </a>
             <Link to="/donate" className="bg-white text-primary-forest border-2 border-primary-forest px-8 py-3 rounded-full font-bold hover:bg-green-50 transition-colors shadow-[4px_4px_0px_#2E7D32] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              Support a Classroom
            </Link>
          </div>
        </div>
      </BentoCard>

    </main>
  );
};

export default Programs;